// @ts-nocheck
import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { EvidencePreviewModal } from "@/components/evidence/EvidencePreviewModal";
import { getEvidenceUrl, refFromValue } from "@/lib/evidenceAccess";

import { DomainPill } from "@/components/ui/DomainPill";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { withOrgName } from "@/lib/resolveOrgTokens";
import { DOMAIN_ORDER, DOMAIN_LABELS, type DomainCode } from "@/lib/functions";
import { supabase } from "@/integrations/supabase/client";
import { Eye, Download, ChevronLeft, ChevronDown, Link2, FileText, ChevronsUpDown, ChevronsDownUp, CheckCircle2, AlertCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { inferKind } from "@/lib/storage";
import { getMyRoles } from "@/lib/auth/roles";
import { resolveOrgTokens } from "@/lib/resolveOrgTokens";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { SaveChip } from "@/components/ui/SaveChip";
import { Modal } from "@/components/ui/Modal";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EvidencePreview, getEvidenceCanonicalKey, openEvidenceInNewTab } from "@/components/reviewer/EvidencePreview";
// ScoreCompare removed – implementation status badges used instead
import { SimpleTooltip } from "@/components/ui/SimpleTooltip";

import { ImplementationStatusBadge } from "@/components/reviewer/ImplementationStatusBadge";
import { ReviewerControlPills } from "@/components/reviewer/ReviewerControlPills";
import { fetchActiveReviewerAssessment } from "@/lib/fetchActiveReviewerAssessment";
import { useQuestionOrdinals } from "@/hooks/useQuestionOrdinals";
import { resolveDisplayOrdinal, questionLabel } from "@/lib/questionOrdinals";
import PptxGenJS from "pptxgenjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { renderRiskAnalyticsSection, type RiskAnalyticsData } from "@/reports/pdf/RiskAnalyticsCharts";
import { renderCapabilitySnapshot, type CsvDomainScore } from "@/reports/pdf/CapabilitySnapshot";
import { renderTopStrategicRisks, type FindingRow } from "@/reports/pdf/TopStrategicRisks";
import { renderThirdPartyRiskSummary, type FindingForThirdParty } from "@/reports/pdf/ThirdPartyRiskSummary";
import { resolveExecSummaryForPdf } from "@/reports/telecom/executiveScaffold";
import { csvDomainNarrative, nistFunctionNarrative } from "@/reports/telecom/domainNarratives";
import { averageToMaturity } from "@/reports/telecom/executiveVisuals";
import { buildTelecomContextBlock } from "@/reports/telecom/operatingContext";
import { useDisplayName } from "@/hooks/useDisplayName";
import { GenerateReportDropdown } from "@/components/reviewer/GenerateReportDropdown";
import { generateReviewerReport } from "@/reports/reviewer/generateReviewerReport";
import { FindingBuilder, type FindingBuilderRef, type FindingData } from "@/components/reviewer/FindingBuilder";
import { clearValidTemplateControlsCache } from "@/api/fetchValidTemplateControls";
import { AssessmentProfileForm } from "@/components/reviewer/AssessmentProfileForm";
import { ExecutiveSummaryForm } from "@/components/reviewer/ExecutiveSummaryForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataClassificationTab } from "@/components/reviewer/DataClassificationTab";
import { InfrastructureSecurityTab } from "@/components/reviewer/InfrastructureSecurityTab";
import { FindingsTab } from "@/components/reviewer/FindingsTab";
import TechnicalValidationTab from "@/components/reviewer/TechnicalValidationTab";
import { RemediationRoadmapTab } from "@/components/reviewer/RemediationRoadmapTab";
import { CorrectiveActionPlanTab } from "@/components/reviewer/CorrectiveActionPlanTab";
import { AssessmentCommunicationBubble } from "@/components/cap/AssessmentCommunicationBubble";
import { supabase as sbTyped } from "@/integrations/supabase/client";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

type PdfPreviewRenderer = "pdfjs" | "fallback";

const dataUriToUint8Array = (dataUri: string): Uint8Array => {
  const base64 = dataUri.split(",")[1] ?? "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const PdfJsPreview = ({ dataUri }: { dataUri: string }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const canvasRefs = React.useRef<Array<HTMLCanvasElement | null>>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const renderTasks: Array<{ cancel: () => void }> = [];

    const renderPdf = async () => {
      try {
        setError(null);
        const pdf = await pdfjsLib.getDocument({ data: dataUriToUint8Array(dataUri) }).promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);
        await new Promise(requestAnimationFrame);

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNumber);
          const baseViewport = page.getViewport({ scale: 1 });
          const availableWidth = Math.max((containerRef.current?.clientWidth ?? 760) - 32, 320);
          const cssScale = Math.min(availableWidth / baseViewport.width, 1.45);
          const viewport = page.getViewport({ scale: cssScale });
          const canvas = canvasRefs.current[pageNumber - 1];
          if (!canvas) continue;

          const outputScale = window.devicePixelRatio || 1;
          const context = canvas.getContext("2d");
          if (!context) continue;

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;

          const task = page.render({
            canvasContext: context,
            viewport,
            transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
          });
          renderTasks.push(task);
          await task.promise;
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[reviewer-pdf] PDF.js preview render failed", err);
          setError("Preview unavailable in this browser. Please download the PDF to view.");
        }
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
      renderTasks.forEach((task) => task.cancel());
    };
  }, [dataUri]);

  if (error) {
    return (
      <div className="flex h-full min-h-[68vh] items-center justify-center rounded-md border bg-background p-8 text-center">
        <div className="max-w-md space-y-2">
          <p className="text-sm font-medium text-foreground">Preview unavailable in this browser. Please download the PDF to view.</p>
          <p className="text-sm text-muted-foreground">The in-app renderer could not display this PDF, but Download PDF remains available.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full min-h-[68vh] overflow-auto rounded-md border bg-muted/20 px-4 py-5">
      {pageCount === 0 ? (
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">Preparing preview…</div>
      ) : (
        <div className="flex flex-col items-center gap-5">
          {Array.from({ length: pageCount }, (_, index) => (
            <canvas
              key={index}
              ref={(node) => { canvasRefs.current[index] = node; }}
              className="max-w-full bg-background shadow-lg"
              aria-label={`Report preview page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};


const sb: any = supabase as any;

type AssignmentRow = {
  assessment_id: string;
  org_id: string;
  org_name: string | null;
  org_type: string | null;
  status: string | null;
  submitted_at: string | null;
  created_at: string | null;
};

const ReviewerConsole = () => {
  console.log("[ReviewerConsole] Component is loading/mounting");
  // Bust the in-memory remediation_templates cache once per mount so newly
  // migrated/added templates surface in the Findings dropdown without
  // requiring a hard browser refresh. Cheap (one Map.clear) and safe.
  useEffect(() => {
    clearValidTemplateControlsCache();
  }, []);
  const navigate = useNavigate();
  const displayName = useDisplayName();
  
  const [searchParams] = useSearchParams();
  const { assessmentId: urlAssessmentId } = useParams<{ assessmentId: string }>();
  // State management for the enhanced reviewer console
  const [selectedOrgType, setSelectedOrgType] = useState<string>("All Types");
  const [selectedOrgName, setSelectedOrgName] = useState<string>("");
  const [isMasterReviewer, setIsMasterReviewer] = useState(false);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>("");
  const [startable, setStartable] = useState<Array<{ assessment_id: string; org_type: string; org_name: string; status: string; submitted_at: string | null }>>([]); // kept for potential future use
  const [availableAssessments, setAvailableAssessments] = useState<Array<{
    assessment_id: string;
    org_id?: string;
    ts: string;
    status: string;
    org_name: string;
    org_type: string;
  }>>([]);
  const [reviewerData, setReviewerData] = useState<any[]>([]);
  // Phase 2A closeout — frozen per-assessment question ordinals (never index-derived).
  const { data: questionOrdinals } = useQuestionOrdinals(selectedAssessmentId || null);
  const [loading, setLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    evidenceList: Array<{ path: string; name: string; type: "image" | "pdf" | "other" }>;
    startIndex: number;
    questionTitle?: string;
  }>({
    isOpen: false,
    evidenceList: [],
    startIndex: 0
  });
  const [reportPreview, setReportPreview] = useState<{
    isOpen: boolean;
    dataUri: string | null;
    filename: string;
    renderer: PdfPreviewRenderer;
  }>({ isOpen: false, dataUri: null, filename: "", renderer: "fallback" });
  const [evidence, setEvidence] = useState<any[]>([]);
  const [headerInfo, setHeaderInfo] = useState<{org_name?: string, org_type?: string, current_review_cycle?: number} | null>(null);
  const [reviewCycle, setReviewCycle] = useState<number | null>(null);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [accordionMode, setAccordionMode] = useState<"single" | "all">("single");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [accordionStateRestored, setAccordionStateRestored] = useState(false);
  const [validationMessage, setValidationMessage] = useState<{
    missingScores: number;
    missingNotes: number;
    missingFindings: number;
    pending: number;
  } | null>(null);
  const [findings, setFindings] = useState<Record<number, Partial<FindingData>[]>>({});
  const findingRefs = React.useRef<Record<number, FindingBuilderRef | null>>({});
  const findingAutosaveTimers = React.useRef<Record<number, ReturnType<typeof setTimeout> | null>>({});
  const persistedFindingsRef = React.useRef<Record<number, Partial<FindingData>[]>>({});
  const [markedReviewed, setMarkedReviewed] = useState<Set<number>>(new Set());
  const [nistRefreshKey, setNistRefreshKey] = useState(0);
  const [csvDomainRefreshKey, setCsvDomainRefreshKey] = useState(0);
  const [activeQuestionnaireKey, setActiveQuestionnaireKey] = useState<string>("telecom_v2");
  const [activeTab, setActiveTab] = useState<string>("assessment-results");
  // Bumped whenever the user switches tabs, so downstream tabs that read
  // findings/owners/due-date data refetch from the canonical
  // assessment_findings rows instead of showing a stale local cache.
  // This keeps Assessment Results, Findings, and Remediation Roadmap in
  // sync when ownership/due-date edits are made in any of those views.
  const [tabReloadKey, setTabReloadKey] = useState(0);
  React.useEffect(() => {
    setTabReloadKey((k) => k + 1);
    if (activeTab === "assessment-results" && selectedAssessmentId) {
      void loadFindings(selectedAssessmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const validationCounts = useMemo(() => {
    if (!reviewerData.length) return { missingScores: 0, missingNotes: 0, missingFindings: 0, pending: 0, total: 0, isComplete: true };
    const missingScores = reviewerData.filter(r => r.reviewer_score == null).length;
    const missingNotes = reviewerData.filter(r => !r.reviewer_notes || r.reviewer_notes.trim().length === 0).length;
    const pending = reviewerData.filter(r => !markedReviewed.has(r.question_id)).length;
    // Check for missing findings on scores 0 or 50
    const missingFindings = reviewerData.filter(r => {
      if (r.reviewer_score === null || r.reviewer_score > 75) return false;
      const ref = findingRefs.current[r.question_id];
      return !ref || !ref.isComplete();
    }).length;
    return {
      missingScores,
      missingNotes,
      missingFindings,
      pending,
      total: reviewerData.length,
      isComplete: missingScores === 0 && missingNotes === 0 && missingFindings === 0,
    };
  }, [reviewerData, findings, markedReviewed]);

  const closeReportPreview = useCallback(() => {
    setReportPreview({ isOpen: false, dataUri: null, filename: "", renderer: "fallback" });
  }, []);

  // --- Accordion persistence helpers ---
  const getAccordionStorageKey = useCallback(() => {
    if (!selectedAssessmentId || !myUserId) return null;
    return `reviewerAccordionState:${selectedAssessmentId}:${myUserId}`;
  }, [selectedAssessmentId, myUserId]);

  const persistAccordionState = useCallback((items: string[], mode: "single" | "all") => {
    const key = getAccordionStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({ expandedQuestionIds: items, mode }));
    } catch { /* noop */ }
  }, [getAccordionStorageKey]);

  // Restore saved state or fall back to first-pending default
  useEffect(() => {
    if (!reviewerData.length || !myUserId || !selectedAssessmentId) {
      setAccordionStateRestored(false);
      return;
    }

    const key = getAccordionStorageKey();
    if (key) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const saved = JSON.parse(raw) as { expandedQuestionIds: string[]; mode?: "single" | "all" };
          const validIds = new Set(reviewerData.map(r => `${r.assessment_id}-${r.question_id}`));
          const restored = (saved.expandedQuestionIds ?? []).filter((id: string) => validIds.has(id));
          setAccordionMode(saved.mode ?? (restored.length > 1 ? "all" : "single"));
          setExpandedItems(restored);
          setAccordionStateRestored(true);
          return;
        }
      } catch { /* fall through to default */ }
    }

    // Default: expand first pending question
    const firstPending = reviewerData.find(r => !markedReviewed.has(r.question_id));
    const target = firstPending ?? reviewerData[0];
    const defaultId = target ? `${target.assessment_id}-${target.question_id}` : null;
    setAccordionMode("single");
    setExpandedItems(defaultId ? [defaultId] : []);
    setAccordionStateRestored(true);
  }, [reviewerData, myUserId, selectedAssessmentId, getAccordionStorageKey]);

  const handleAccordionChange = useCallback((value: string[]) => {
    let next: string[];
    if (accordionMode === "single") {
      next = value.length > 0 ? [value[value.length - 1]] : [];
    } else {
      next = value;
    }
    setExpandedItems(next);
    persistAccordionState(next, accordionMode);
  }, [accordionMode, persistAccordionState]);

  const expandAll = useCallback(() => {
    const all = reviewerData.map(r => `${r.assessment_id}-${r.question_id}`);
    setAccordionMode("all");
    setExpandedItems(all);
    persistAccordionState(all, "all");
  }, [reviewerData, persistAccordionState]);

  const collapseAll = useCallback(() => {
    setAccordionMode("single");
    setExpandedItems([]);
    persistAccordionState([], "single");
  }, [persistAccordionState]);


  const finalAssessmentId = urlAssessmentId || searchParams.get("assessment_id") || "";

  useEffect(() => {
    (async () => {
      const roles = await getMyRoles();
      setIsMasterReviewer(roles.includes("master_reviewer") || roles.includes("admin"));

      const { data: { user } } = await supabase.auth.getUser();
      setMyUserId(user?.id ?? null);
    })();
  }, []);

  async function loadAssignedAssessmentsForReviewer() {
    // Use reviewer_list_assignments RPC instead of direct .from("assessment_assignees")
    const { data, error } = await sb.rpc('reviewer_list_assignments');

    if (error) {
      console.error("[ReviewerConsole] fetch error", error);
      return [];
    }

    console.log("[ReviewerConsole] assigned rows", data);

    return (data ?? []).map((row: any) => ({
      assessment_id: row.assessment_id,
      status: row.status,
      org_id: row.org_id,
      org_name: row.org_name ?? "—",
      questionnaire_id: row.questionnaire_id,
      created_at: row.created_at,
    }));
  }

  // Load reviewer's active assignments
  useEffect(() => {
    console.log("[ReviewerConsole] useEffect triggered - loading assignments");
    (async () => {
      try {
        const rows = await loadAssignedAssessmentsForReviewer();
        console.log("[ReviewerConsole] loadAssignedAssessmentsForReviewer returned:", rows);
        
        const mappedRows = rows.map(item => ({
          assessment_id: item.assessment_id,
          org_id: item.org_id,
          ts: item.created_at,
          status: item.status,
          org_name: item.org_name,
          org_type: '' // Not available in assessment_assignees query
        }));
        
        console.log("[ReviewerConsole] Setting availableAssessments to:", mappedRows);
        setAvailableAssessments(mappedRows);
      } catch (error) {
        console.error("[ReviewerConsole] Error loading assignments:", error);
      }
    })();
  }, []);

  // Initialize selection (URL param wins if valid; else first/only)
  useEffect(() => {
    if (!availableAssessments.length) return;

    // If URL has an assessmentId and it's valid, use it
    if (urlAssessmentId && availableAssessments.some(a => a.assessment_id === urlAssessmentId)) {
      setSelectedAssessmentId(urlAssessmentId);
      return;
    }

    // Else default: if exactly one assignment, pick it; otherwise pick the most recent
    setSelectedAssessmentId(availableAssessments[0].assessment_id);
  }, [availableAssessments, urlAssessmentId]);

  // Auto-load data when selectedAssessmentId changes
  useEffect(() => {
    if (selectedAssessmentId) {
      loadAssessmentDataDirectly(selectedAssessmentId);
    }
  }, [selectedAssessmentId]);

  // Load header info for the current assessment
  useEffect(() => {
    (async () => {
      if (!selectedAssessmentId) return;

      // Use reviewer_get_assessment_detail RPC (no direct table access)
      const { data: detailRows, error: detailErr } = await (supabase as any).rpc('reviewer_get_assessment_detail', {
        p_assessment_id: selectedAssessmentId,
      });

      const detail = Array.isArray(detailRows) ? detailRows[0] : detailRows;
      if (!detailErr && detail) {
        setHeaderInfo(prev => ({
          ...prev,
          org_name: detail.org_name,
          org_type: detail.org_category,
        }));
      } else {
        setHeaderInfo(null);
      }
    })();
  }, [selectedAssessmentId]);

  
  // Load assessment data directly (bypassing org selection)
  const loadAssessmentDataDirectly = async (assessmentId: string) => {
    if (!assessmentId) return;
    
    try {
      // Use reviewer_get_assessment_detail RPC instead of direct .from('assessments')
      const { data: detailRows, error: detailError } = await sb.rpc('reviewer_get_assessment_detail', {
        p_assessment_id: assessmentId
      });

      if (detailError) {
        console.error('Failed to load assessment details:', detailError);
        // fallback for admins - keep going
      }

      const detail = Array.isArray(detailRows) ? detailRows[0] : detailRows;
      if (detail) {
        setSelectedOrgName(detail.org_name ?? 'Unknown Portfolio');
        setSelectedOrgType(detail.org_category ?? '');
        setHeaderInfo(prev => ({ ...prev, org_name: detail.org_name, org_type: detail.org_category }));
        setReviewCycle(detail.current_review_cycle);
      }
      
      // Load the reviewer data
      await loadReviewerData(assessmentId);
      await loadEvidence(assessmentId);
      await loadFindings(assessmentId);
      await upsertReviewerProgress(assessmentId);
    } catch (error) {
      console.error('Error loading assessment data directly:', error);
    }
  };

  
// Load available assessments from the updated query
const loadAvailableAssessments = async (orgType: string, orgName: string) => {
  if (!orgType || !orgName) return;

  try {
    // Use reviewer_list_assignments RPC - filter client-side by org name
    const { data, error } = await sb.rpc('reviewer_list_assignments');

    if (error) {
      console.error('load reviewer assignments error', error);
      setAvailableAssessments([]);
      setSelectedAssessmentId("");
      return;
    }

    const formatted = (data || [])
      .filter((item: any) => item.org_name === orgName)
      .map((item: any) => ({
        assessment_id: item.assessment_id,
        org_id: item.org_id,
        ts: item.created_at,
        status: item.status,
        org_name: item.org_name || '',
        org_type: item.org_category || orgType
      }));
    setAvailableAssessments(formatted);
    setSelectedAssessmentId(formatted[0]?.assessment_id || "");
  } catch (err) {
    console.error("loadAvailableAssessments error:", err);
    setAvailableAssessments([]);
    setSelectedAssessmentId("");
  }
};

// Helper to get current user ID
const getMyUserId = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) throw error || new Error("No auth user");
  return user.id;
};

// Helper to upsert reviewer progress via RPC
const upsertReviewerProgress = async (assessmentId: string, opts?: { lastQuestionId?: number; lastDomain?: string }) => {
  try {
    const { error } = await sb.rpc('reviewer_upsert_progress', {
      p_assessment_id: assessmentId,
      p_last_question_id: opts?.lastQuestionId ?? null,
      p_last_domain: opts?.lastDomain ?? null,
    });
    if (error) console.warn("reviewer_upsert_progress warning:", error);
  } catch (e) {
    console.warn("reviewer_upsert_progress failed:", e);
  }
};

// 3) Load reviewer Q&A rows from tables
const loadReviewerData = async (assessmentId: string) => {
  if (!assessmentId) return;

  setLoading(true);
  try {
    // Check if user is admin first (we'll use this for multiple queries)
    const { data: isAdminRpc } = await sb.rpc('current_user_is_admin');
    console.log('[ReviewerConsole] isAdminRpc=', isAdminRpc, 'assessmentId=', assessmentId);
    
    // Get assessment's questionnaire_id - admin uses RPC, non-admin uses direct query
    let questionnaireId: string | null = null;
    
    if (isAdminRpc) {
      const { data: qidData } = await sb.rpc('admin_get_assessment_questionnaire_id', {
        p_assessment_id: assessmentId
      });
      questionnaireId = qidData?.[0]?.questionnaire_id ?? null;
    } else {
      // Reviewer path: use reviewer_get_assessment_detail RPC
      const { data: detailRows } = await sb.rpc('reviewer_get_assessment_detail', {
        p_assessment_id: assessmentId
      });
      const detail = Array.isArray(detailRows) ? detailRows[0] : detailRows;
      questionnaireId = detail?.questionnaire_id ?? null;
    }

    if (!questionnaireId) {
      console.warn('No questionnaire found for assessment:', assessmentId);
      setReviewerData([]);
      setLoading(false);
      return;
    }
    setActiveQuestionnaireKey(questionnaireId);

    // Get questionnaire items with questions - admin uses RPC, non-admin uses direct query
    let questionsData: any[] | null = null;
    
    if (isAdminRpc) {
      const { data: itemsData } = await sb.rpc('admin_list_questionnaire_items', {
        p_questionnaire_id: questionnaireId
      });
      
      if (itemsData?.length) {
        // Fetch questions data via admin RPC for basic fields
        // questions table typically has open RLS for reading, but use direct query for full fields
        const questionIds = itemsData.map((item: any) => item.question_id);
        const { data: qData } = await supabase
          .from('questions')
          .select('id, question_key, domain, title, question_text, evidence_types, answer_scale, controls')
          .in('id', questionIds);
        
        // Map to expected format
        const questionMap = new Map((qData ?? []).map(q => [q.id, q]));
        questionsData = itemsData.map((item: any) => ({
          order_index: item.order_index,
          questions: questionMap.get(item.question_id)
        })).filter((item: any) => item.questions);
      }
    } else {
      // Reviewer path: use reviewer_get_questionnaire_items RPC
      const { data: rpcItems, error: rpcItemsErr } = await sb.rpc('reviewer_get_questionnaire_items', {
        p_assessment_id: assessmentId
      });
      if (rpcItemsErr) {
        console.error('Load questions error:', rpcItemsErr);
        toast({
          title: "Load error",
          description: `Failed to load questions: ${rpcItemsErr.message}`,
          variant: "destructive"
        });
        setReviewerData([]);
        return;
      }
      // Map RPC result to expected format
      questionsData = (rpcItems ?? []).map((item: any) => ({
        order_index: item.order_index,
        questions: {
          id: item.question_id,
          question_key: item.question_key,
          domain: item.domain,
          title: item.title,
          question_text: item.question_text,
          evidence_types: item.evidence_types,
          answer_scale: item.answer_scale,
          controls: item.controls ?? [],
        }
      }));
    }

    if (!questionsData?.length) {
      console.warn('No questions found for questionnaire:', questionnaireId);
      setReviewerData([]);
      setLoading(false);
      return;
    }

    // Get responses for this assessment - use admin RPC to bypass RLS
    const questionIds = questionsData.map(item => item.questions.id);
    
    let responsesData: any[] | null = null;
    if (isAdminRpc) {
      console.log('[ReviewerConsole] Using admin_list_responses RPC');
      // Use canonical admin_list_responses RPC
      const { data: rpcData, error: rpcError } = await (sb.rpc as any)('admin_list_responses', {
        p_assessment_id: assessmentId,
        p_question_ids: questionIds.map(Number),
      });
      if (rpcError) {
        console.error('[ReviewerConsole] admin_list_responses failed:', rpcError);
      }
      responsesData = rpcData;
    } else {
      // Reviewer path: use reviewer_get_responses RPC
      console.log('[ReviewerConsole] Using reviewer_get_responses RPC');
      const { data: rpcResp } = await sb.rpc('reviewer_get_responses', {
        p_assessment_id: assessmentId
      });
      responsesData = rpcResp;
    }

    // Get reviewer ratings for this assessment - use admin RPC if admin
    let ratingsData: any[] | null = null;
    if (isAdminRpc) {
      console.log('[ReviewerConsole] Using admin_list_reviewer_ratings RPC');
      const { data: rpcRatings, error: rpcRatingsErr } = await sb.rpc('admin_list_reviewer_ratings', {
        p_assessment_id: assessmentId,
      });
      if (rpcRatingsErr) {
        console.error('[ReviewerConsole] admin_list_reviewer_ratings failed:', rpcRatingsErr);
      }
      ratingsData = rpcRatings;
    } else {
      // Reviewer path: use reviewer_get_ratings RPC
      const { data: rpcRatings2, error: rpcRatingsErr2 } = await sb.rpc(
        'reviewer_get_ratings',
        { p_assessment_id: assessmentId }
      );
      if (rpcRatingsErr2) {
        console.error('reviewer_get_ratings error:', rpcRatingsErr2);
      }
      ratingsData = rpcRatings2;
    }

    // Combine all data
    const transformedData = questionsData.map((item: any) => {
      const questionId = item.questions.id;
      const response = responsesData?.find(r => r.question_id === questionId);
      const rating = ratingsData?.find(r => r.question_id === questionId);

      return {
        assessment_id: assessmentId,
        question_id: questionId,
        question_key: item.questions.question_key,
        domain: item.questions.domain,
        title: item.questions.title,
        question_text: item.questions.question_text,
        answer_scale: item.questions.answer_scale as Record<string, string> | null,
        evidence_types: item.questions.evidence_types,
        order_index: item.order_index,
        controls: item.questions.controls ?? [],
        respondent_score: response?.score ?? null,
        respondent_comment: response?.comment || '',
        evidence_links: response?.evidence_links || [],
        answered_at: response?.updated_at || null,
        reviewer_score: rating?.reviewer_score ?? null,
        reviewer_notes: rating?.reviewer_notes ?? '',
        reviewer_user_id: rating?.reviewer_user_id || null
      };
    });

    // Sort by domain order (GV→ID→PR→DE→RS→RC) then order_index
    const sorted = [...transformedData].sort((a, b) => {
      const da = DOMAIN_ORDER.indexOf(a.domain as DomainCode);
      const db = DOMAIN_ORDER.indexOf(b.domain as DomainCode);
      const domA = da === -1 ? 999 : da;
      const domB = db === -1 ? 999 : db;
      if (domA !== domB) return domA - domB;
      return (a.order_index ?? 0) - (b.order_index ?? 0);
    });

    console.log('Reviewer data loaded:', { count: sorted.length, assessmentId });
    // Initialize markedReviewed from DB data: rows that already have both score and notes
    const alreadyReviewed = new Set<number>();
    sorted.forEach((r: any) => {
      if (r.reviewer_score != null && r.reviewer_notes && r.reviewer_notes.trim().length > 0) {
        alreadyReviewed.add(r.question_id);
      }
    });
    setMarkedReviewed(alreadyReviewed);
    setReviewerData(sorted);

  } catch (error: any) {
    console.error("Error loading reviewer data:", error);
    toast({
      title: "Load error",
      description: `Failed to load reviewer data: ${error.message}`,
      variant: "destructive"
    });
    setReviewerData([]);
  } finally {
    setLoading(false);
  }
};

const normalizeEvidenceRows = (rows: any[]) => {
  const deduped = new Map<string, any>();
  for (const row of rows || []) {
    const file_path = row.file_url || row.link_url || '';
    if (!file_path) continue;
    const normalized = {
      ...row,
      file_path,
      mime_type: row.evidence_type === 'link' ? 'text/url' : null,
      uploaded_at: row.created_at,
    };
    const key = `${row.question_id}:${getEvidenceCanonicalKey(file_path)}`;
    const existing = deduped.get(key);
    if (!existing || new Date(normalized.uploaded_at).getTime() < new Date(existing.uploaded_at).getTime()) {
      deduped.set(key, normalized);
    }
  }

  return Array.from(deduped.values()).sort((a, b) => {
    if (a.question_id !== b.question_id) return Number(a.question_id) - Number(b.question_id);
    return new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();
  });
};

// Load evidence for the selected assessment via secure RPC
const loadEvidence = async (assessmentId: string) => {
  if (!assessmentId) return;

  try {
    const { data, error } = await (supabase.rpc as any)(
      'get_assessment_evidence',
      { p_assessment_id: assessmentId }
    );

    if (error) {
      console.error('Load evidence error:', error);
      toast({
        title: "Load error",
        description: `Failed to load evidence: ${error.message}`,
        variant: "destructive"
      });
      setEvidence([]);
      return;
    }

    const mapped = normalizeEvidenceRows(data || []);
    setEvidence(mapped);
    console.log("[ReviewerConsole] evidence loaded via RPC:", mapped);
  } catch (error: any) {
    console.error("Error loading evidence:", error);
    toast({
      title: "Load error",
      description: `Failed to load evidence: ${error.message}`,
      variant: "destructive"
    });
    setEvidence([]);
  }
};

// Load findings for the selected assessment via secure RPC
const loadFindings = async (assessmentId: string) => {
  if (!assessmentId) return;
  try {
    const { data, error } = await sb.rpc('reviewer_get_findings', {
      p_assessment_id: assessmentId,
    });
    if (error) {
      console.warn('Load findings warning:', error);
      setFindings({});
      return;
    }

    // Group findings by question_id (supports multiple controls/findings per question)
    const map: Record<number, Partial<FindingData>[]> = {};
    for (const f of (data || [])) {
      const questionId = Number(f.question_id);
      if (!map[questionId]) map[questionId] = [];
      map[questionId].push({
        id: f.id ?? undefined,
        finding_title: f.finding_title ?? '',
        priority_level: f.priority_level ?? '',
        recommended_action: f.recommended_action ?? '',
        timeframe: f.timeframe ?? '',
        recommended_measures: f.recommended_measures ?? '',
        remediation_path: f.remediation_path ?? '',
        risk_level: f.risk_level ?? '',
        risk_override: f.risk_override ?? false,
        control_code: f.control_code ?? '',
        owners: Array.isArray(f.owners) ? f.owners : [],
        due_date: f.due_date ?? null,
      });
    }

    setFindings(map);
    persistedFindingsRef.current = map;
    console.log('[ReviewerConsole] findings loaded:', { assessmentId, rows: (data || []).length });
  } catch (e) {
    console.warn('loadFindings error:', e);
    setFindings({});
    persistedFindingsRef.current = {};
  }
};

const hasFindingContent = (finding: Partial<FindingData>) => {
  return !!(
    finding.finding_title?.trim() ||
    finding.recommended_action?.trim() ||
    finding.recommended_measures?.trim() ||
    finding.remediation_path?.trim()
  );
};

const syncQuestionFindings = useCallback((questionId: number, nextFindings: FindingData[]) => {
  if (!selectedAssessmentId || reviewSubmitted) return;

  const existingFindings = persistedFindingsRef.current[questionId] ?? [];

  if (findingAutosaveTimers.current[questionId]) {
    clearTimeout(findingAutosaveTimers.current[questionId]!);
  }

  findingAutosaveTimers.current[questionId] = setTimeout(async () => {
    const nextWithContent = nextFindings.filter(hasFindingContent);
    const prevCodes = new Set(existingFindings.map((f) => f.control_code).filter(Boolean));
    const nextCodes = new Set(nextWithContent.map((f) => f.control_code).filter(Boolean));

    try {
      for (const findingData of nextWithContent) {
        const payload = {
          p_assessment_id: selectedAssessmentId,
          p_question_id: questionId,
          p_control_code: findingData.control_code || null,
          p_finding_title: findingData.finding_title || null,
          p_priority_level: findingData.priority_level || null,
          p_recommended_action: findingData.recommended_action || null,
          p_timeframe: findingData.timeframe || null,
          p_recommended_measures: findingData.recommended_measures || null,
          p_remediation_path: findingData.remediation_path || null,
          p_risk_level: findingData.risk_level || null,
          p_risk_override: findingData.risk_override ?? false,
          p_owners: (findingData as any).owners ?? [],
          p_due_date: (findingData as any).due_date ?? null,
        };

        console.log('[ReviewerConsole] autosaving finding:', payload);
        const { error } = await sb.rpc('reviewer_upsert_finding', payload);
        if (error) throw error;
      }

      for (const controlCode of prevCodes) {
        if (!nextCodes.has(controlCode)) {
          const { error } = await sb.rpc('reviewer_delete_finding', {
            p_assessment_id: selectedAssessmentId,
            p_question_id: questionId,
            p_control_code: controlCode,
          });
          if (error) throw error;
        }
      }

      persistedFindingsRef.current[questionId] = nextWithContent.map((entry) => ({ ...entry }));
    } catch (error) {
      console.error('[ReviewerConsole] finding autosave failed:', { questionId, error });
    }
  }, 400);
}, [selectedAssessmentId, reviewSubmitted]);

useEffect(() => {
  return () => {
    Object.values(findingAutosaveTimers.current).forEach((timer) => {
      if (timer) clearTimeout(timer);
    });
  };
}, []);

// Download all evidence for a specific question
const downloadAllEvidenceForQuestion = async (questionId: number) => {
  if (!selectedAssessmentId) return;

  try {
    // Get all evidence files for this question via secure RPC
    const { data: rpcData, error } = await (supabase.rpc as any)(
      'get_assessment_evidence',
      { p_assessment_id: selectedAssessmentId }
    );
    const evidenceFiles = normalizeEvidenceRows(rpcData || [])
      .filter((e: any) => e.question_id === questionId)
      .map((e: any) => ({
        file_path: e.file_url || e.link_url || '',
        mime_type: e.evidence_type === 'link' ? 'text/url' : null,
      }));

    if (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        variant: 'destructive'
      });
      return;
    }

    if (!evidenceFiles || evidenceFiles.length === 0) {
      toast({
        title: 'No files found',
        description: 'No evidence files found for this question.',
        variant: 'destructive'
      });
      return;
    }

    // Download each file
    for (const file of evidenceFiles) {
      try {
        if (file.mime_type === 'text/url') {
          window.open(file.file_path, '_blank', 'noopener,noreferrer');
          continue;
        }
        // ACTION 2: bulk download is brokered server-side (attachment mode).
        const url = await getEvidenceUrl(refFromValue(file.file_path), {
          disposition: "attachment",
          ttl: 900,
        });

        // Open in new tab/window for download
        window.open(url, '_blank');

      } catch (fileError) {
        console.error('Failed to download', file.file_path, fileError);
      }
    }

    toast({
      title: 'Download started',
      description: `Downloading ${evidenceFiles.length} file(s) for Question ${questionId}`
    });

  } catch (error: any) {
    console.error('Download error:', error);
    toast({
      title: 'Download failed',
      description: error.message || 'Failed to download files',
      variant: 'destructive'
    });
  }
};

  async function generatePptForAssessment(
    assessmentId: string,
    orgType: string,
    orgName: string
  ) {
    // 1) Fetch QnA rows for this assessment
    const { data, error } = await supabase
      .from("v_reviewer_qna")
      .select("*")
      .eq("assessment_id", assessmentId)
      .order("domain", { ascending: true })
      .order("order_index", { ascending: true });

    if (error) {
      console.error("PPT fetch error:", error);
      throw new Error(error.message || "Failed to load report data");
    }
    const rows = data || [];

    // 2) Basic aggregates
    const totalQs = rows.length;
    const answered = rows.filter(r => r.respondent_score !== null && r.respondent_score !== undefined).length;
    const withEvidence = rows.filter(r => (r.evidence_links?.length > 0)).length;

    // 3) Group by domain
    const byDomain: Record<string, typeof rows> = {};
    for (const r of rows) {
      const key = r.domain || "General";
      if (!byDomain[key]) byDomain[key] = [];
      byDomain[key].push(r);
    }

    // 4) Create PPT
    const pptx = new PptxGenJS();

    // Theme colors (adjust later to your brand)
    const PRIMARY = "1F4E79";   // deep blue
    const ACCENT  = "0B8043";   // green
    const TEXT    = "333333";   // dark gray
    const MUTED   = "777777";   // muted gray

    // Title slide
    const titleSlide = pptx.addSlide();
    titleSlide.background = { color: "FFFFFF" };
    titleSlide.addText("Cybersecurity Risk & Maturity Assessment", {
      x: 0.5, y: 0.7, w: 9, h: 1,
      fontSize: 28, bold: true, color: PRIMARY
    });
    titleSlide.addText(`${orgName}  •  ${orgType}`, {
      x: 0.5, y: 1.7, w: 9, h: 0.5,
      fontSize: 18, color: TEXT
    });
    titleSlide.addText(`Assessment: ${assessmentId.slice(0,8)}…`, {
      x: 0.5, y: 2.3, w: 9, h: 0.4,
      fontSize: 12, color: MUTED
    });
    titleSlide.addText(new Date().toLocaleString(), {
      x: 0.5, y: 2.8, w: 9, h: 0.4,
      fontSize: 12, color: MUTED
    });

    // Summary slide
    const sum = pptx.addSlide();
    sum.addText("Summary", { x: 0.5, y: 0.5, fontSize: 22, bold: true, color: PRIMARY });
    const bullets = [
      `Total Questions: ${totalQs}`,
      `Answered by Respondent: ${answered}`,
      `Items with Evidence: ${withEvidence}`,
    ];
    sum.addText(bullets.map(b => `• ${b}`).join("\n"), {
      x: 0.5, y: 1.3, w: 9, h: 2.5,
      fontSize: 14, color: TEXT, lineSpacing: 16
    });

    // Domain slides with compact table
    for (const [domain, list] of Object.entries(byDomain)) {
      const s = pptx.addSlide();
      s.addText(domain, { x: 0.5, y: 0.5, fontSize: 20, bold: true, color: PRIMARY });

      // Build a small table: Q#, Question (trimmed), Resp, Rev, Ev
      const header = [
        { text: "Q#", options: { bold: true, color: "FFFFFF" } },
        { text: "Question", options: { bold: true, color: "FFFFFF" } },
        { text: "Resp%", options: { bold: true, color: "FFFFFF" } },
        { text: "Rev%", options: { bold: true, color: "FFFFFF" } },
        { text: "Ev", options: { bold: true, color: "FFFFFF" } },
      ];

      const rowsForTable = list.map(r => {
        const q = (r.question_text || "").replace(/\s+/g, " ").trim();
        const resolvedQ = resolveOrgTokens(q, (rows[0] as any)?.org_name);
        const shortQ = resolvedQ.length > 80 ? resolvedQ.slice(0,77) + "…" : resolvedQ;
        return [
          String(r.order_index ?? r.question_id),
          shortQ,
          r.respondent_score === null || r.respondent_score === undefined ? "—" : `${r.respondent_score}%`,
          r.reviewer_score   === null || r.reviewer_score   === undefined ? "—" : `${r.reviewer_score}%`,
          String(r.evidence_links?.length ?? 0),
        ];
      });

      const tableData = [header, ...rowsForTable];

      s.addTable(tableData, {
        x: 0.5, y: 1.2, w: 9,
        colW: [0.7, 6.2, 0.9, 0.9, 0.5],
        border: { color: "DDDDDD", pt: 1 },
        fill: "FFFFFF",
        fontSize: 11,
        align: "left",
        rowH: 0.3,
        // zebra stripes
        alternateRowFill: "F7F9FB",
        // header row style
        autoPage: true,
        autoPageRepeatHeader: true,
        autoPageLineWeight: 0,
        autoPageCharWeight: 0,
        // header fill
        // (pptxgen v3 header is row 0)
        // we can style via `tableRowHeight` + `tableCellFill` per row,
        // but simplest is to use `fill` on first row via cell options above
        // which we set to white text; now set header row background:
        // workaround: add a rectangle under header to "fake" header band
      });

      // Add a background band for header row
      s.addShape(pptx.ShapeType.rect, {
        x: 0.5, y: 1.2, w: 9, h: 0.3,
        fill: PRIMARY, line: { color: PRIMARY }
      });
    }

    // 5) Save
    const safeName = `${orgName || "Org"}_${assessmentId.slice(0,8)}_RiskAssessment`.replace(/[^\w\-]+/g, "_");
    await pptx.writeFile({ fileName: `${safeName}.pptx` });
  }

  const { toast } = useToast();

  // Per-question validation helper
  const getQuestionValidation = useCallback((row: any): { valid: boolean; message: string | null } => {
    const score = row.reviewer_score;
    const hasNotes = !!(row.reviewer_notes && row.reviewer_notes.trim().length > 0);

    // No score selected yet
    if (score === null || score === undefined) {
      return { valid: false, message: "A reviewer score must be selected before marking as reviewed." };
    }

    // Score 100 (Implemented): require notes
    if (score === 100) {
      if (!hasNotes) {
        return { valid: false, message: "Reviewer Notes are required when a control is marked as Implemented." };
      }
      return { valid: true, message: null };
    }

    // Score 0 or 50: require notes + completed finding
    if (score <= 75) {
      if (!hasNotes) {
        return { valid: false, message: "Reviewer Notes are required before marking this question as reviewed." };
      }
      const findingRef = findingRefs.current[row.question_id];
      if (!findingRef || !findingRef.isComplete()) {
        return { valid: false, message: "A finding must be fully completed before marking this question as reviewed." };
      }
      return { valid: true, message: null };
    }

    return { valid: true, message: null };
  }, []);

  // Handle mark reviewed action - saves, collapses current, opens next
  const handleMarkReviewed = async (row: any, currentIndex: number) => {
    // Validate before proceeding
    const validation = getQuestionValidation(row);
    if (!validation.valid) {
      toast({
        title: "Cannot mark as reviewed",
        description: validation.message,
        variant: "destructive",
      });
      return;
    }

    const score =
      row.reviewer_score ??
      row.respondent_score ??
      50;

    try {
      await saveReviewerInputsSilent(row.question_id, score, row.reviewer_notes);

      // Save all findings for this question
      const findingRef = findingRefs.current[row.question_id];
      if (findingRef && findingRef.hasData()) {
        const findingsArray = findingRef.getData();
        for (const findingData of findingsArray) {
          const payload = {
            p_assessment_id: row.assessment_id,
            p_question_id: row.question_id,
            p_control_code: findingData.control_code || null,
            p_finding_title: findingData.finding_title || null,
            p_priority_level: findingData.priority_level || null,
            p_recommended_action: findingData.recommended_action || null,
            p_timeframe: findingData.timeframe || null,
            p_recommended_measures: findingData.recommended_measures || null,
            p_remediation_path: findingData.remediation_path || null,
            p_risk_level: findingData.risk_level || null,
            p_risk_override: findingData.risk_override ?? false,
          };
          console.log('[ReviewerConsole] mark-reviewed finding upsert:', payload);
          const { error } = await sb.rpc('reviewer_upsert_finding', payload);
          if (error) throw error;
        }
      }

      await loadFindings(row.assessment_id);

      // Optimistically update local UI
      setReviewerData(prev =>
        prev.map(r =>
          r.assessment_id === row.assessment_id && r.question_id === row.question_id
            ? { ...r, reviewer_score: score }
            : r
        )
      );
      // Mark this question as explicitly reviewed
      setMarkedReviewed(prev => new Set(prev).add(row.question_id));

      // Auto-advance: close current, open next
      const nextIndex = currentIndex + 1;
      if (nextIndex < reviewerData.length) {
        const nextRow = reviewerData[nextIndex];
        const nextValue = `${nextRow.assessment_id}-${nextRow.question_id}`;
        setExpandedItems([nextValue]);
        persistAccordionState([nextValue], "single");
        setAccordionMode("single");

        // Scroll next item into view after DOM update
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-value="${nextValue}"]`);
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        // Last question - just collapse
        setExpandedItems([]);
        persistAccordionState([], "single");
      }
    } catch (e) {
      console.error("Couldn't mark reviewed:", e);
    }
  };

  // Organization dropdown state - no longer needed with smart dropdown
  // const [availableOrgTypes, setAvailableOrgTypes] = useState<string[]>([]);
  // const [availableOrgNames, setAvailableOrgNames] = useState<string[]>([]);

  // Load startable assessments for current reviewer
  useEffect(() => {
    (async () => {
      // Use reviewer_list_assignments RPC instead of direct .from('assessment_assignees')
      const { data: assigned, error: assignedErr } = await sb.rpc('reviewer_list_assignments');
      
      if (assignedErr) {
        console.warn('Error loading startable assessments:', assignedErr);
        return;
      }

      const ids = (assigned || []).map((x: any) => x.assessment_id);
      if (!ids.length) { setStartable([]); return; }

      // Use reviewer_check_started RPC instead of direct .from("reviewer_ratings")
      const { data: touched, error: tErr } = await sb.rpc('reviewer_check_started', {
        p_assessment_ids: ids
      });
      if (tErr) {
        console.warn('Error checking reviewer progress:', tErr);
        return;
      }
      const already = new Set((touched || []).map((t: any) => t.assessment_id));

      const list = (assigned || [])
        .filter((a: any) => !already.has(a.assessment_id))
        .map((a: any) => ({
          assessment_id: a.assessment_id,
          org_type: a.org_category || null,
          org_name: a.org_name || null,
          status: a.status,
          submitted_at: a.submitted_at
        }));

      setStartable(list);
    })();
  }, []);

  // Load assessments when org selection changes
  useEffect(() => {
    if (selectedOrgType && selectedOrgName) {
      loadAvailableAssessments(selectedOrgType, selectedOrgName);
    }
  }, [selectedOrgType, selectedOrgName]);

  // Load reviewer data when assessment selection changes
  useEffect(() => {
    if (selectedAssessmentId) {
      loadReviewerData(selectedAssessmentId);
      loadEvidence(selectedAssessmentId);
      // Record progress touch
      upsertReviewerProgress(selectedAssessmentId);
    }
  }, [selectedAssessmentId]);



  // Save reviewer inputs via reviewer_upsert_rating RPC (silent version for autosave)
  const saveReviewerInputsSilent = async (questionId: number, reviewerScore?: number, reviewerNotes?: string) => {
    if (!selectedAssessmentId) return;
    
    const { error } = await sb.rpc('reviewer_upsert_rating', {
      p_assessment_id: selectedAssessmentId,
      p_question_id: questionId,
      p_reviewer_score: reviewerScore ?? null,
      p_reviewer_notes: reviewerNotes ?? null,
    });

    if (error) throw error;

    // Update reviewer progress with last question ID
    await upsertReviewerProgress(selectedAssessmentId, { lastQuestionId: questionId });
  };

  // Save reviewer inputs to reviewer_ratings table
  const saveReviewerInputs = async (questionId: number, reviewerScore?: number, reviewerNotes?: string) => {
    if (!selectedAssessmentId) return;
    
    try {
      await saveReviewerInputsSilent(questionId, reviewerScore, reviewerNotes);

      // Recalculate NIST + CSV domain scores after each rating change
      try {
        await Promise.all([
          sb.rpc("calculate_nist_function_scores", { p_assessment_id: selectedAssessmentId }),
          sb.rpc("calculate_csv_domain_scores", { p_assessment_id: selectedAssessmentId }),
        ]);
        setNistRefreshKey(k => k + 1);
        setCsvDomainRefreshKey(k => k + 1);
      } catch (calcErr: any) {
        console.warn("[ReviewerConsole] Score recalc failed (non-blocking):", calcErr.message);
      }

      console.log("Reviewer update saved:", {
        assessment_id: selectedAssessmentId,
        question_id: questionId,
        reviewer_score: reviewerScore
      });

      toast({
        title: "Reviewer update saved",
        description: "Your score and notes have been saved successfully"
      });

      // Reload data to show updated values
      await loadReviewerData(selectedAssessmentId);
    } catch (error: any) {
      console.error("Error saving reviewer inputs:", error);
      toast({
        title: "Error saving review",
        description: error.message
      });
    }
  };

  // Helper: map numeric score to implementation band
  function mapScoreToImplementation(score: number | null): string | null {
    if (score === null) return null;
    if (score <= 25) return "Not Implemented";
    if (score <= 75) return "Partially Implemented";
    return "Implemented";
  }

  // Helper: map numeric score to maturity label using answer_scale keys
  const SCORE_TO_LABEL: Record<number, string> = {
    0: "Nonexistent", 25: "Initial", 50: "Developing", 75: "Defined/Managed", 100: "Optimized"
  };

  function getMaturityInfo(score: number | null | undefined, answerScale: Record<string, string> | null | undefined) {
    if (score == null) return null;
    const label = SCORE_TO_LABEL[score] ?? Object.keys(SCORE_TO_LABEL).reduce((best, k) => {
      const n = Number(k);
      return Math.abs(n - score) < Math.abs(Number(best) - score) ? k : best;
    }, "0");
    const maturityLabel = SCORE_TO_LABEL[Number(label)] ?? "Unknown";
    const description = answerScale?.[maturityLabel] ?? null;
    return { label: maturityLabel, description };
  }

  const IMPL_TO_SCORE: Record<string, number> = {
    "Not Implemented": 0,
    "Partially Implemented": 50,
    "Implemented": 100,
  };

  // Reviewer Score Cell Component
  const ReviewerScoreCell = ({ row }: { row: any }) => {
    const { state, begin, succeed, fail } = useAutosaveIndicator(150, 1500);
    const [implLabel, setImplLabel] = React.useState<string>(
      mapScoreToImplementation(row.reviewer_score) ?? ""
    );

    React.useEffect(() => { 
      setImplLabel(mapScoreToImplementation(row.reviewer_score) ?? ""); 
    }, [row.reviewer_score]);

    const handleChange = (value: string) => {
      setImplLabel(value);
      begin();
      const numericScore = IMPL_TO_SCORE[value];
      if (numericScore === undefined) { fail(); return; }
      
      saveReviewerInputsSilent(row.question_id, numericScore, undefined)
        .then(() => {
          succeed();
          setReviewerData(prev => prev.map(item => 
            item.question_id === row.question_id 
              ? { ...item, reviewer_score: numericScore }
              : item
          ));
        })
        .catch(() => fail());
    };

    return (
      <div className="flex items-center gap-2">
        <Select value={implLabel} onValueChange={handleChange} disabled={reviewSubmitted}>
          <SelectTrigger className="w-[200px] rounded-xl border-slate-300 text-sm shadow-sm">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Not Implemented">Not Implemented</SelectItem>
            <SelectItem value="Partially Implemented">Partially Implemented</SelectItem>
            <SelectItem value="Implemented">Implemented</SelectItem>
          </SelectContent>
        </Select>
        <SaveChip state={state} />
      </div>
    );
  };

  // Reviewer Notes Cell Component - uses ref-stable callbacks to avoid remount
  const ReviewerNotesCellInner = React.memo(({ 
    questionId, 
    initialNotes, 
    disabled,
    onSave 
  }: { 
    questionId: number; 
    initialNotes: string; 
    disabled: boolean;
    onSave: (questionId: number, notes: string) => Promise<void>;
  }) => {
    const { state, begin, succeed, fail } = useAutosaveIndicator();
    const [value, setValue] = useState(initialNotes);
    const valueRef = React.useRef(value);
    valueRef.current = value;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    };

    const handleBlur = () => {
      const current = valueRef.current;
      if (current === initialNotes) return; // no change
      begin();
      onSave(questionId, current)
        .then(() => succeed())
        .catch(() => fail());
    };

    return (
      <div className="space-y-1">
        <textarea
          className="w-full min-h-[140px] max-h-[220px] overflow-auto resize-y rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 disabled:opacity-60 disabled:cursor-not-allowed"
          placeholder="Reviewer notes..."
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
        />
        <div className="flex items-center justify-between">
          <SaveChip state={state} />
          <span className="text-[11px] text-slate-400">
            {value?.length ?? 0} chars
          </span>
        </div>
      </div>
    );
  });
  ReviewerNotesCellInner.displayName = "ReviewerNotesCellInner";

  // Stable callback for notes save (doesn't change on re-render)
  const handleNotesSave = useCallback(async (questionId: number, notes: string) => {
    await saveReviewerInputsSilent(questionId, undefined, notes);
    setReviewerData(prev => prev.map(item =>
      item.question_id === questionId
        ? { ...item, reviewer_notes: notes }
        : item
    ));
  }, [selectedAssessmentId]);

  // Evidence Cell Component
  // Evidence lives in `assessment_evidence` (loaded via get_assessment_evidence),
  // NOT in responses.evidence_links — reading only the latter made respondent
  // uploads and links look like "no evidence" in the reviewer view.
  const EvidenceCell = ({ row }: { row: any }) => {
    const [open, setOpen] = React.useState(false);
    const fromResponses: string[] = Array.isArray(row.evidence_links) ? row.evidence_links : [];
    const fromCanonical: string[] = (evidence || [])
      .filter((e: any) => Number(e.question_id) === Number(row.question_id))
      .map((e: any) => e.file_path || e.file_url || e.link_url)
      .filter(Boolean);
    const urls: string[] = Array.from(new Set([...fromCanonical, ...fromResponses]));


    const count = urls.length;
    const has = count > 0;

    return (
      <>
        <button
          type="button"
          onClick={() => has && setOpen(true)}
          className={`inline-flex items-center gap-2 rounded-lg px-2 py-1 text-sm ring-1 ring-inset ${
            has ? "bg-slate-50 text-slate-800 ring-slate-200 hover:bg-slate-100" : "bg-slate-50 text-slate-400 ring-slate-200 cursor-not-allowed"
          }`}
          title={has ? `View ${count} attachment${count>1?"s":""}` : "No evidence"}
        >
          {has ? "📎" : "✕"} {has ? count : ""}
        </button>

        <Modal 
          open={open} 
          onClose={() => setOpen(false)} 
          title={`Evidence — ${questionLabel(resolveDisplayOrdinal(questionOrdinals, row.question_id, "ReviewerConsole.evidence"))}`}
          footer={
            count > 0 ? (
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">{count} attachment{count>1?"s":""}</div>
                <div className="flex gap-2">
                  {urls.map((u, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => openEvidenceInNewTab(u)}
                      className="rounded-md border px-3 py-1 text-sm hover:bg-slate-50"
                    >
                      Open {i+1}
                    </button>
                  ))}
                </div>
              </div>
            ) : null
          }
        >
          <EvidencePreview urls={urls} />
        </Modal>
      </>
    );
  };
  
  
  // Removed old org type/name loading functions - replaced with smart assignment dropdown
  
// 4) Evidence preview: call the evidence view
const handleEvidencePreview = async (questionId: number, questionText: string) => {
  if (!selectedAssessmentId) return;

  try {
    const { data: rpcEvidence, error } = await (supabase.rpc as any)(
      'get_assessment_evidence',
      { p_assessment_id: selectedAssessmentId }
    );

    if (error) {
      console.error('Load evidence error:', { 
        rpc: 'get_assessment_evidence', 
        assessmentId: selectedAssessmentId, 
        questionId, 
        error 
      });
      toast({
        title: "Error loading evidence", 
        description: `Failed to load evidence: ${error.message}`,
        variant: "destructive"
      });
      return;
    }

    // Filter to requested question and map to expected shape
    const evidence = normalizeEvidenceRows(rpcEvidence || [])
      .filter((e: any) => e.question_id === questionId)
      .map((e: any) => ({
        ...e,
        file_path: e.file_url || e.link_url || '',
        mime_type: e.evidence_type === 'link' ? 'text/url' : null,
      }));

    console.log('Evidence loaded via RPC:', { 
      count: evidence.length, 
      assessmentId: selectedAssessmentId, 
      questionId 
    });

    if (evidence && evidence.length > 0) {
      setPreviewModal({
        isOpen: true,
        evidenceList: evidence.map(e => ({
          path: e.file_path,
          name: e.file_path.split('/').pop() || '',
          type: inferKind(e.mime_type, e.file_path)
        })),
        startIndex: 0,
        questionTitle: questionText
      });
    } else {
      toast({
        title: "No evidence",
        description: "No evidence uploaded for this question."
      });
    }
  } catch (error: any) {
    console.error("Error loading evidence:", error);
    toast({
      title: "Error loading evidence", 
      description: `Failed to load evidence: ${error.message}`,
      variant: "destructive"
    });
  }
};

  return (
    <AppLayout title="Reviewer Console | Cybersecurity Assessment" description="Review respondent submissions and provide your assessment.">
      {/* Sticky header region */}
      <div className="sticky top-0 z-50 bg-background border-b pb-4 -mx-6 px-6 pt-2">
      {/* Breadcrumb navigation */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
        <ChevronLeft className="h-4 w-4" />
        <button
          onClick={() => navigate("/reviewer")}
          className="hover:underline hover:text-foreground transition-colors"
        >
          Reviewer Dashboard
        </button>
        <span>/</span>
        <span className="text-foreground font-medium">
          {headerInfo?.org_name ?? selectedOrgName ?? "Review"}
          {" Review"}
        </span>
        {reviewCycle != null && (
          <>
            <span>/</span>
            <span className="text-muted-foreground text-xs">
              Cycle {reviewCycle}
            </span>
          </>
        )}
      </nav>

      {(() => {
        const currentAssessment = availableAssessments.find(a => a.assessment_id === selectedAssessmentId);
        const isLocked = currentAssessment?.status === "Closed";
        return isLocked ? (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            🔒 Review Locked — This review cycle has been submitted.
            Only a Portfolio Manager can reopen it.
          </div>
        ) : null;
      })()}

        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-semibold">Reviewer Console</h1>
            <p className="text-muted-foreground">
              Review respondent submissions and provide your assessment.
            </p>
            {(headerInfo?.org_name || selectedOrgName) && (
              <p className="text-sm mt-1">
                <span className="font-medium text-foreground/80">{headerInfo?.org_name ?? selectedOrgName}</span>
                {displayName && (
                  <>
                    <span className="mx-2 text-border">|</span>
                    <span className="text-muted-foreground">{displayName}</span>
                  </>
                )}
              </p>
            )}
          </div>

          {/* Reviewer quick actions */}
<div className="flex gap-2">

  {selectedAssessmentId && (
    <Button
      variant="outline"
      onClick={async () => {
        if (!selectedAssessmentId) return;
        const { error } = await sb.rpc('reviewer_mark_reviewed', {
          p_assessment_id: selectedAssessmentId
        });
        if (!error) {
          toast({ title: "Handoff complete", description: "PO can now review" });
          await loadReviewerData(selectedAssessmentId);
        }
      }}
    >
      Send to PO
    </Button>
  )}

  {selectedAssessmentId && (
    <GenerateReportDropdown
      disabled={!selectedAssessmentId}
      onGeneratePpt={async () => {
        if (!selectedAssessmentId) return;
        try {
          await generatePptForAssessment(selectedAssessmentId, selectedOrgType || "", selectedOrgName || "");
          toast({ title: "Report ready", description: "PPT downloaded." });
        } catch (e: any) {
          console.error(e);
          toast({ title: "Export failed", description: e.message || "Could not generate PPT", variant: "destructive" });
        }
      }}
      onPreviewPdf={async () => {
        if (!selectedAssessmentId) return;
        try {
          const current = availableAssessments.find(a => a.assessment_id === selectedAssessmentId);
          const { filename, dataUrl } = await generateReviewerReport({
            assessmentId: selectedAssessmentId,
            orgId: current?.org_id,
            orgName: selectedOrgName || headerInfo?.org_name || "",
            mode: "preview",
          });
          if (!dataUrl) {
            setReportPreview({ isOpen: true, dataUri: null, filename, renderer: "fallback" });
            toast({ title: "Preview not available", description: "Download to view the PDF.", variant: "destructive" });
            return;
          }
          setReportPreview({ isOpen: true, dataUri: dataUrl, filename, renderer: "pdfjs" });
          toast({ title: "Preview ready", description: "Report preview opened in the app." });
        } catch (e: any) {
          console.error(e);
          toast({ title: "Preview failed", description: e.message || "Could not preview PDF", variant: "destructive" });
        }
      }}
      onGeneratePdf={async () => {
        if (!selectedAssessmentId) return;
        try {
          const current = availableAssessments.find(a => a.assessment_id === selectedAssessmentId);
          // org_id is optional — generator will look it up if missing.
          const { filename } = await generateReviewerReport({
            assessmentId: selectedAssessmentId,
            orgId: current?.org_id,
            orgName: selectedOrgName || headerInfo?.org_name || "",
          });
          toast({ title: "PDF Report ready", description: `${filename} downloaded.` });
        } catch (e: any) {
          console.error(e);
          toast({ title: "PDF Export failed", description: e.message || "Could not generate PDF", variant: "destructive" });
        }
      }}
    />
  )}
  {selectedAssessmentId && reviewerData.length > 0 && (
    <>
      <Button variant="outline" size="sm" onClick={expandAll}>
        <ChevronsUpDown className="h-4 w-4 mr-1" /> Expand All
      </Button>
      <Button variant="outline" size="sm" onClick={collapseAll}>
        <ChevronsDownUp className="h-4 w-4 mr-1" /> Collapse All
      </Button>
    </>
  )}
        </div>
        </div>
      </div>{/* end sticky header */}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
        <TabsList className="mb-4">
          <TabsTrigger value="profile">Assessment Profile</TabsTrigger>
          <TabsTrigger value="executive-summary">Executive Summary (Live)</TabsTrigger>
          <TabsTrigger value="assessment-results">Assessment Results</TabsTrigger>
          <TabsTrigger value="data-classification">Data Classification</TabsTrigger>
          {activeQuestionnaireKey === "telecom_v2" && (
            <TabsTrigger value="infrastructure-security">Infrastructure Security</TabsTrigger>
          )}
          {activeQuestionnaireKey === "telecom_v2" && (
            <TabsTrigger value="technical-validation">Technical Validation</TabsTrigger>
          )}
          <TabsTrigger value="findings">Findings</TabsTrigger>
          <TabsTrigger value="corrective-action-plan">Corrective Action Plan</TabsTrigger>
          <TabsTrigger value="remediation-roadmap">Remediation Roadmap</TabsTrigger>
        </TabsList>

        <TabsContent value="technical-validation">
          {selectedAssessmentId ? (
            <TechnicalValidationTab
              assessmentId={selectedAssessmentId}
              disabled={reviewSubmitted}
            />
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to view technical validation findings.
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile">
          {selectedAssessmentId ? (
            <AssessmentProfileForm assessmentId={selectedAssessmentId} disabled={reviewSubmitted} />
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to view its profile.
            </div>
          )}
        </TabsContent>

        <TabsContent value="findings">
          {selectedAssessmentId ? (
            <FindingsTab
              assessmentId={selectedAssessmentId}
              disabled={reviewSubmitted}
              reloadKey={tabReloadKey}
              questionnaireKey={activeQuestionnaireKey}
              reviewerData={reviewerData.map((r: any) => ({
                question_id: r.question_id,
                domain: r.domain,
                title: r.title,
                controls: r.controls ?? [],
              }))}
              onNavigateToQuestion={(questionId) => {
                setActiveTab("assessment-results");
                // Expand the target question accordion
                const itemKey = `${selectedAssessmentId}-${questionId}`;
                setExpandedItems([itemKey]);
                setAccordionMode("single");
                // Scroll to the question after tab switch
                setTimeout(() => {
                  const el = document.getElementById(`question-${questionId}`);
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 150);
              }}
            />
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to view findings.
            </div>
          )}
        </TabsContent>

        <TabsContent value="remediation-roadmap">
          {selectedAssessmentId ? (
            <RemediationRoadmapTab
              assessmentId={selectedAssessmentId}
              disabled={reviewSubmitted}
              questionnaireKey={activeQuestionnaireKey}
              reloadKey={tabReloadKey}
              onNavigateToFinding={(questionId) => {
                setActiveTab("findings");
              }}
            />
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to view the remediation roadmap.
            </div>
          )}
        </TabsContent>

        <TabsContent value="corrective-action-plan">
          {selectedAssessmentId ? (
            <CorrectiveActionPlanTab
              assessmentId={selectedAssessmentId}
              disabled={reviewSubmitted}
              questionnaireKey={activeQuestionnaireKey}
            />
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to view the corrective action plan.
            </div>
          )}
        </TabsContent>

        <TabsContent value="data-classification">
          {selectedAssessmentId ? (
            <DataClassificationTab assessmentId={selectedAssessmentId} disabled={reviewSubmitted} />
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to manage data classification.
            </div>
          )}
        </TabsContent>
        {activeQuestionnaireKey === "telecom_v2" && (
          <TabsContent value="infrastructure-security">
            {selectedAssessmentId ? (
              <InfrastructureSecurityTab
                assessmentId={selectedAssessmentId}
                disabled={reviewSubmitted}
                onChanged={async () => {
                  try {
                    await sb.rpc("calculate_csv_domain_scores", { p_assessment_id: selectedAssessmentId });
                  } catch (err: any) {
                    console.warn("[ReviewerConsole] CSV recalc after Infrastructure save failed:", err?.message ?? err);
                  } finally {
                    setCsvDomainRefreshKey((k) => k + 1);
                  }
                }}
              />
            ) : (
              <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
                Select an assessment to manage infrastructure security.
              </div>
            )}
          </TabsContent>
        )}
        <TabsContent value="executive-summary">
          {selectedAssessmentId ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground italic">
                Auto-generated from assessment results, findings, and reviewer inputs
              </p>
              <ExecutiveSummaryForm assessmentId={selectedAssessmentId} disabled={reviewSubmitted} nistRefreshKey={nistRefreshKey} csvDomainRefreshKey={csvDomainRefreshKey} questionnaireKey={activeQuestionnaireKey} orgName={selectedOrgName || headerInfo?.org_name || null} />
            </div>
          ) : (
            <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
              Select an assessment to view the executive summary.
            </div>
          )}
        </TabsContent>

        <TabsContent value="assessment-results">
      <div>
           {/* Smart Assignment Dropdown */}
           {!urlAssessmentId && assignments.length > 0 && (
             <div className="bg-card rounded-lg border p-4 mb-4">
               <h1 className="text-xl font-semibold">
                 {assignments.length === 1 ? (
                   <div className="flex items-center gap-2">
                     <span>Reviewing: {options[0]?.label}</span>
                     <Badge variant="secondary" className="text-slate-700">
                       {options[0]?.meta}
                     </Badge>
                   </div>
                 ) : (
                   <div className="space-y-2">
                     <span className="text-base">Select Assignment:</span>
                     <div className="max-w-md">
                       <Select
                         value={selectedAssessmentId}
                         onValueChange={(val) => setSelectedAssessmentId(val)}
                       >
                         <SelectTrigger className="w-full">
                           <SelectValue placeholder="Select organization / assessment" />
                         </SelectTrigger>
                         <SelectContent>
                           {options.map(opt => (
                             <SelectItem key={opt.value} value={opt.value}>
                               <div className="flex flex-col">
                                 <span className="text-sm">{opt.label}</span>
                                 {opt.meta && <span className="text-[11px] text-muted-foreground">{opt.meta}</span>}
                               </div>
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     </div>
                   </div>
                 )}
               </h1>
             </div>
           )}

            {/* Empty state if no assignments */}
            {!urlAssessmentId && myUserId && assignments.length === 0 && (
              <div className="bg-card rounded-lg border p-4 mb-4">
                <div className="text-sm text-muted-foreground">
                  No active assessments assigned for review. If you were just assigned, refresh in a moment—or contact your admin.
                </div>
              </div>
            )}
      </div>


      <div className="space-y-4">

        {/* Question Cards */}
        {loading ? (
          <div className="bg-card shadow-sm rounded-lg border p-8 text-center text-muted-foreground">
            Loading assessment data...
          </div>
        ) : availableAssessments.length === 0 && selectedOrgType && selectedOrgName ? (
          <div className="bg-card shadow-sm rounded-lg border p-8 text-center text-muted-foreground">
            No assessments found for this Type/Org. <br/>
            <span className="text-xs">
              Try selecting a different Organization Type or use "All Types".
            </span>
          </div>
        ) : reviewerData.length === 0 && selectedAssessmentId ? (
          <div className="bg-card shadow-sm rounded-lg border p-8 text-center text-muted-foreground">
            No answers found for this assessment.
          </div>
        ) : !selectedAssessmentId ? (
          <div className="bg-card shadow-sm rounded-lg border p-8 text-center text-muted-foreground">
            Please select an organization and assessment to view the data.
          </div>
        ) : (() => {
          // Group rows by domain for section headers
          let lastDomain: string | null = null;
          const items: React.ReactNode[] = [];

          reviewerData.forEach((row, index) => {
            const showHeader = row.domain !== lastDomain;
            lastDomain = row.domain;
            const domainLabel = DOMAIN_LABELS[row.domain as DomainCode] ?? row.domain;
            const itemValue = `${row.assessment_id}-${row.question_id}`;
            const selfLabel = row.respondent_score != null
              ? (SCORE_TO_LABEL[row.respondent_score] ?? "Unknown")
              : "No answer";
            const reviewerStatus = markedReviewed.has(row.question_id) ? "Reviewed" : "Pending";
            const questionEvidence = evidence.filter(e => Number(e.question_id) === Number(row.question_id));
            const evCount = questionEvidence.length;

            if (showHeader) {
              items.push(
                <div key={`header-${row.domain}`} className="flex items-center gap-3 pt-6 pb-2 first:pt-0">
                  <DomainPill code={row.domain} />
                  <h2 className="text-lg font-semibold text-foreground">{domainLabel}</h2>
                  <div className="flex-1 border-t border-border" />
                </div>
              );
            }

            items.push(
              <AccordionItem key={itemValue} value={itemValue} data-value={itemValue} id={`question-${row.question_id}`} className="bg-card shadow-sm rounded-lg border mb-2 overflow-hidden">
                {/* Collapsed Summary Header */}
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50 transition-colors [&[data-state=open]]:bg-muted/30">
                  <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <DomainPill code={row.domain} />
                    <span className="text-sm font-medium text-muted-foreground shrink-0">{questionLabel(resolveDisplayOrdinal(questionOrdinals, row.question_id, "ReviewerConsole"))}</span>
                    <span className="text-sm font-semibold text-foreground truncate">
                      {row.title || resolveOrgTokens(row.question_text, selectedOrgName)?.slice(0, 60)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
                    {/* Completion indicator */}
                    {(() => {
                      const hasScore = row.reviewer_score != null;
                      const hasNotes = !!(row.reviewer_notes && row.reviewer_notes.trim().length > 0);
                      if (hasScore && hasNotes) {
                        return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
                      }
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="relative flex h-3 w-3 shrink-0 cursor-help">
                              <span className="absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" avoidCollisions className="z-[1000]">
                            {!hasScore && !hasNotes ? "Missing score & notes" : !hasScore ? "Missing score" : "Missing notes"}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })()}
                    {row.reviewer_score != null ? (
                      <ImplementationStatusBadge valueScore={row.reviewer_score} />
                    ) : (
                      <span className="bg-muted text-muted-foreground px-3 py-1 rounded-md text-sm font-medium">
                        Not Scored
                      </span>
                    )}
                    <StatusBadge status={reviewerStatus} />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      📎 {evCount}
                    </span>
                  </div>
                </AccordionTrigger>

                {/* Expanded Detail Panel */}
                <AccordionContent className="px-4 pb-4">
                  {/* Question Content */}
                  <div className="mb-3 pt-2">
                    <div className="text-xs text-muted-foreground">{row.question_key} • {row.domain}</div>
                    <div className="text-base font-semibold text-foreground leading-snug">
                      {row.title || resolveOrgTokens(row.question, selectedOrgName)}
                    </div>
                    {row.question_text && (
                      <div className="text-sm whitespace-pre-wrap text-muted-foreground mt-1">
                        {resolveOrgTokens(row.question_text, selectedOrgName)}
                      </div>
                    )}
                    {/* Control pills with reviewer guidance */}
                    <ReviewerControlPills controls={row.controls ?? []} currentDomain={row.domain} />
                  </div>

                  {/* Respondent Submission Section */}
                  <div className="mt-2 rounded-md border bg-muted/50 p-4 space-y-3">
                    <div className="text-sm font-semibold text-foreground/80 uppercase tracking-wide">Respondent Submission</div>

                    {/* Selected Answer */}
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-0.5">Selected Answer</div>
                      {row.respondent_score != null ? (() => {
                        const maturityLabel = SCORE_TO_LABEL[row.respondent_score] ?? "Unknown";
                        const selectedAnswerText = row.answer_scale?.[maturityLabel] ?? null;
                        return (
                          <div className="text-sm text-foreground">
                            <span className="font-medium">{maturityLabel}</span>
                            {selectedAnswerText && (
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{selectedAnswerText}</p>
                            )}
                          </div>
                        );
                      })() : (
                        <p className="text-sm italic text-destructive">No answer submitted.</p>
                      )}
                    </div>

                    {/* Respondent Comment */}
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-0.5">Respondent Comment</div>
                      {row.respondent_comment ? (
                        <p className="text-sm text-foreground/80 whitespace-pre-wrap">{row.respondent_comment}</p>
                      ) : (
                        <p className="text-sm italic text-muted-foreground">No comment provided.</p>
                      )}
                    </div>

                    {/* Evidence (inline) */}
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-0.5">Evidence</div>
                      {questionEvidence.length === 0 ? (
                        <p className="text-sm italic text-muted-foreground">No evidence provided.</p>
                      ) : (
                          <div className="flex flex-col gap-1">
                          {questionEvidence.map((e, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => openEvidenceInNewTab(e.file_path)}
                              className="inline-flex items-center gap-1 text-left text-sm text-primary hover:underline"
                            >
                              {e.mime_type === 'text/url' ? '🔗 Open Link' : '📎 Download File'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scores + Evidence Row */}
                  <div className="flex gap-6 mt-3">
                    <div className="flex-1">
                      <label className="text-muted-foreground text-xs uppercase font-medium">Respondent Self-Assessment</label>
                      <div className="mt-1">
                        <ImplementationStatusBadge valueScore={row.respondent_score} />
                      </div>
                    </div>

                    <div className="flex-1">
                      <label className="text-muted-foreground text-xs uppercase font-medium">Reviewer Assessment</label>
                      <div className="mt-1 space-y-2">
                        <ImplementationStatusBadge valueScore={row.reviewer_score} />
                        <ReviewerScoreCell row={row} />
                      </div>
                    </div>
                  </div>

                  {/* Reviewer Notes - Full Width */}
                  <div className="mt-4">
                    <label className="text-muted-foreground text-xs uppercase font-medium mb-2 block">Reviewer Notes</label>
                    <ReviewerNotesCellInner 
                      questionId={row.question_id} 
                      initialNotes={row.reviewer_notes ?? ""} 
                      disabled={reviewSubmitted}
                      onSave={handleNotesSave}
                    />
                  </div>

                  {/* Finding Builder */}
                  <FindingBuilder
                    ref={(el) => { findingRefs.current[row.question_id] = el; }}
                    reviewerScore={row.reviewer_score}
                    controls={row.controls ?? []}
                    existingFindings={findings[row.question_id] ?? null}
                    disabled={reviewSubmitted}
                    required={row.reviewer_score !== null && row.reviewer_score <= 75}
                    hidden={row.reviewer_score === 100}
                    questionnaireKey={activeQuestionnaireKey}
                    questionKey={row.question_key}
                    onFindingsChange={(entries) => syncQuestionFindings(row.question_id, entries)}
                  />


                  {/* Mark Reviewed */}
                  <div className="mt-3 flex justify-end">
                    {(() => {
                      const validation = getQuestionValidation(row);
                      return (
                        <SimpleTooltip text={validation.valid ? "Mark this question as reviewed" : (validation.message ?? "Complete required fields")}>
                          <button
                            type="button"
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm transition-colors ${
                              validation.valid
                                ? "border-green-600 bg-green-600 text-white hover:bg-green-700"
                                : "border-border bg-muted text-muted-foreground cursor-not-allowed opacity-60"
                            }`}
                            onClick={() => handleMarkReviewed(row, index)}
                          >
                            Mark reviewed
                          </button>
                        </SimpleTooltip>
                      );
                    })()}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          });

          return (
            <TooltipProvider delayDuration={200}>
              <Accordion
                type="multiple"
                value={expandedItems}
                onValueChange={handleAccordionChange}
                className="space-y-1"
              >
                {items}
              </Accordion>
            </TooltipProvider>
          );
        })()}
      </div>


      {/* Validation Summary */}
      {validationMessage && (
        <div className="mx-auto max-w-lg rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-destructive text-sm mb-2">You must complete the review before submission:</p>
              <ul className="text-sm text-destructive/90 space-y-1 list-disc list-inside">
                {validationMessage.missingScores > 0 && (
                  <li>{validationMessage.missingScores} question{validationMessage.missingScores > 1 ? 's' : ''} missing Reviewer score</li>
                )}
                {validationMessage.missingNotes > 0 && (
                  <li>{validationMessage.missingNotes} question{validationMessage.missingNotes > 1 ? 's' : ''} missing Reviewer notes</li>
                )}
                {validationMessage.missingFindings > 0 && (
                  <li>{validationMessage.missingFindings} question{validationMessage.missingFindings > 1 ? 's' : ''} scored Not Implemented or Partially Implemented without a completed finding</li>
                )}
                {validationMessage.pending > 0 && (
                  <li>{validationMessage.pending} question{validationMessage.pending > 1 ? 's' : ''} still marked as Pending</li>
                )}
              </ul>
              <button
                className="mt-2 text-xs text-destructive/70 hover:text-destructive underline"
                onClick={() => setValidationMessage(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Review button removed — to be relocated. */}
        </TabsContent>
      </Tabs>

      <EvidencePreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal(prev => ({ ...prev, isOpen: false }))}
        evidenceList={previewModal.evidenceList}
        startIndex={previewModal.startIndex}
        questionTitle={previewModal.questionTitle}
      />

      <Dialog open={reportPreview.isOpen} onOpenChange={(open) => !open && closeReportPreview()}>
        <DialogContent className="max-w-6xl h-[88vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="border-b px-5 py-4">
            <DialogTitle>Report Preview</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 bg-muted/30 p-4">
            {reportPreview.dataUri && reportPreview.renderer === "pdfjs" ? (
              <PdfJsPreview dataUri={reportPreview.dataUri} />
            ) : (
              <div className="flex h-full min-h-[68vh] items-center justify-center rounded-md border bg-background p-8 text-center">
                <div className="max-w-md space-y-2">
                  <p className="text-sm font-medium text-foreground">Preview unavailable in this browser. Please download the PDF to view.</p>
                  <p className="text-sm text-muted-foreground">Download PDF remains available.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t px-5 py-4">
            <Button variant="outline" onClick={closeReportPreview}>Close Preview</Button>
            <Button
              onClick={async () => {
                if (reportPreview.dataUri) {
                  const link = document.createElement("a");
                  link.href = reportPreview.dataUri;
                  link.download = reportPreview.filename || "Cybersecurity_Report.pdf";
                  link.click();
                  return;
                }
                // Fallback: re-generate in download mode if we never got a data URL.
                if (!selectedAssessmentId) return;
                try {
                  const current = availableAssessments.find(a => a.assessment_id === selectedAssessmentId);
                  await generateReviewerReport({
                    assessmentId: selectedAssessmentId,
                    orgId: current?.org_id,
                    orgName: selectedOrgName || headerInfo?.org_name || "",
                    mode: "download",
                  });
                } catch (e: any) {
                  toast({ title: "Download failed", description: e.message || "Could not generate PDF", variant: "destructive" });
                }
              }}
            >
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssessmentCommunicationBubble
        assessmentId={selectedAssessmentId}
        orgName={selectedOrgName || headerInfo?.org_name || null}
      />
    </AppLayout>
  );
};

export default ReviewerConsole;